import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiDollarSign, FiTrendingUp, FiShield, FiClock, FiTarget, FiCheck, FiMail, FiMessageSquare } = FiIcons;

const ForLawyersPage = () => {
  const benefits = [
    {
      icon: FiTarget,
      title: "Ready-to-Hire Clients",
      description: "Get connected with clients who are actively seeking legal services and ready to pay"
    },
    {
      icon: FiDollarSign,
      title: "No Commission Fees",
      description: "Keep 100% of what you charge. We don't take any percentage from your private deals"
    },
    {
      icon: FiUsers,
      title: "Targeted Matching",
      description: "Our AI only sends you clients that match your practice areas and expertise"
    },
    {
      icon: FiClock,
      title: "Save Time on Marketing",
      description: "Stop spending time and money on ads. Focus on what you do best - practicing law"
    },
    {
      icon: FiShield,
      title: "Verified Clients",
      description: "All client requests are screened to ensure they're legitimate legal needs"
    },
    {
      icon: FiTrendingUp,
      title: "Grow Your Practice",
      description: "Consistently get new clients and build a sustainable practice"
    }
  ];

  const howItWorks = [
    {
      step: "1",
      title: "Create Your Profile",
      description: "Set up your lawyer profile with practice areas, experience, and discount offers"
    },
    {
      step: "2",
      title: "Get Matched",
      description: "Our AI matches you with clients who need your specific legal expertise"
    },
    {
      step: "3",
      title: "Connect & Convert",
      description: "Contact interested clients directly and close deals at your rates"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson, Esq.",
      practice: "Family Law Attorney",
      quote: "LegalCoupon has transformed my practice. I get 5-10 qualified leads per month and my client base has grown by 200%.",
      avatar: "SJ"
    },
    {
      name: "Michael Chen, Esq.",
      practice: "Business Law Firm",
      quote: "The quality of leads is exceptional. Clients come pre-qualified and ready to hire. Best investment for my practice.",
      avatar: "MC"
    },
    {
      name: "Lisa Rodriguez, Esq.",
      practice: "Immigration Attorney",
      quote: "I've been able to reduce my marketing spend by 80% while getting better clients. The platform just works.",
      avatar: "LR"
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="hero-gradient text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              Get Ready-to-Hire Clients<br />
              <span className="text-green-300">Without Ads</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl md:text-2xl mb-8 text-blue-100 max-w-3xl mx-auto"
            >
              Join 500+ lawyers who get quality clients through our AI-powered matching platform
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <Link
                to="/pricing"
                className="bg-white text-blue-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-all transform hover:scale-105 shadow-lg"
              >
                Join as a Lawyer
              </Link>
              <a
                href="#how-it-works"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-700 transition-all"
              >
                See How It Works
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 text-center"
            >
              <div>
                <div className="text-3xl font-bold mb-2">500+</div>
                <div className="text-blue-100">Active Lawyers</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">2,500+</div>
                <div className="text-blue-100">Monthly Leads</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">85%</div>
                <div className="text-blue-100">Client Conversion Rate</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Lawyers Choose LegalCoupon
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Stop wasting money on ads that don't convert. Get clients who are ready to hire.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 p-6 rounded-lg card-hover"
              >
                <div className="bg-blue-100 text-blue-700 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <SafeIcon icon={benefit.icon} className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works for Lawyers
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Simple process to start getting quality clients
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorks.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="text-center relative"
              >
                <div className="bg-blue-700 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                  {step.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {step.title}
                </h3>
                <p className="text-gray-600">
                  {step.description}
                </p>
                {index < howItWorks.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full">
                    <div className="w-full h-0.5 bg-gray-300 relative">
                      <div className="absolute right-0 top-0 w-2 h-2 bg-gray-300 rounded-full transform -translate-y-0.5"></div>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Lawyers Are Saying
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Real results from real lawyers using our platform
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="bg-gray-50 p-6 rounded-lg card-hover"
              >
                <div className="flex items-center mb-4">
                  <div className="bg-blue-700 text-white w-12 h-12 rounded-full flex items-center justify-center font-semibold mr-4">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.practice}</div>
                  </div>
                </div>
                <p className="text-gray-600 italic">
                  "{testimonial.quote}"
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Grow Your Practice?
          </h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Join hundreds of lawyers who are getting quality clients every month
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/pricing"
              className="bg-white text-blue-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-all transform hover:scale-105"
            >
              View Pricing Plans
            </Link>
            <a
              href="mailto:lawyers@legalcoupon.com"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-700 transition-all"
            >
              Contact Sales Team
            </a>
          </div>
          <p className="text-sm text-blue-100 mt-6">
            No setup fees • Cancel anytime • 14-day free trial
          </p>
        </div>
      </section>
    </div>
  );
};

export default ForLawyersPage;